import {FunctionComponent, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import ExclamationPointIcon from '@/assets/images/icon-exclamation-point.svg?react';
import {Document, DocumentSignatureStatus} from '@/interfaces/document';
import {AdvisorNameCodeTile} from './advisor-name-code-tile';
import {DocumentStatusActionLabel} from './document-status-action-label';

const SIGNATURE_STATUSES_IN_ERROR = [
  DocumentSignatureStatus.Unknown,
  DocumentSignatureStatus.Declined,
  DocumentSignatureStatus.Expired,
  DocumentSignatureStatus.EmailBounced,
  DocumentSignatureStatus.SignerLocked,
  DocumentSignatureStatus.Deleted,
  DocumentSignatureStatus.Trashed,
];

const SIGNATURE_STATUS_ERROR_TRANSLATION_MAPPING = {
  [DocumentSignatureStatus.Unknown]: 'components.document-menu.document-list.item.content.signature-status.unknown',
  [DocumentSignatureStatus.Declined]: 'components.document-menu.document-list.item.content.signature-status.declined',
  [DocumentSignatureStatus.Expired]: 'components.document-menu.document-list.item.content.signature-status.expired',
  [DocumentSignatureStatus.EmailBounced]:
    'components.document-menu.document-list.item.content.signature-status.email-bounced',
  [DocumentSignatureStatus.SignerLocked]:
    'components.document-menu.document-list.item.content.signature-status.signer-locked',
  [DocumentSignatureStatus.Deleted]: 'components.document-menu.document-list.item.content.signature-status.deleted',
  [DocumentSignatureStatus.Trashed]: 'components.document-menu.document-list.item.content.signature-status.trashed',
} as const;

interface ElementProps {
  document: Document;
  showVersion?: boolean;
}

const ItemContent: FunctionComponent<ElementProps> = ({
  document: {
    signatureStatus,
    status,
    sentForSignatureFailedAt,
    submitFailedAt,
    name,
    type,
    modelVersion,
    owner,
    advisorCode,
    createdAt,
    createdBy,
    updatedAt,
    updatedBy,
    completedAt,
    completedBy,
    sentForSignatureAt,
    sentForSignatureBy,
    submittedAt,
  },
  showVersion,
}) => {
  const {t} = useTranslation();

  const isSignatureStatusInError = useMemo(() => {
    return signatureStatus && Boolean(SIGNATURE_STATUSES_IN_ERROR.includes(signatureStatus));
  }, [signatureStatus]);

  const isInError = useMemo(() => {
    if (status === 'incomplete' && sentForSignatureFailedAt) {
      return true;
    }

    if (status === 'waitingSignature') {
      if (submitFailedAt || isSignatureStatusInError) return true;
    }

    return false;
  }, [sentForSignatureFailedAt, submitFailedAt, status, isSignatureStatusInError]);

  const errorText = useMemo(() => {
    if (status !== 'waitingSignature' || !isSignatureStatusInError) return;

    return SIGNATURE_STATUS_ERROR_TRANSLATION_MAPPING[
      signatureStatus as keyof typeof SIGNATURE_STATUS_ERROR_TRANSLATION_MAPPING
    ];
  }, [signatureStatus, isSignatureStatusInError, status]);

  const statusText = useMemo(() => {
    switch (status) {
      case 'incomplete':
        return {
          i18nStatusKey: 'modified-at',
          date: updatedAt,
          contact: updatedBy,
        } as const;
      case 'complete':
        return {
          i18nStatusKey: 'completed-at',
          date: completedAt,
          contact: completedBy,
        } as const;
      case 'waitingSignature':
        return {
          i18nStatusKey: 'sent-at',
          date: sentForSignatureAt,
          contact: sentForSignatureBy,
        } as const;
      case 'sent':
        return {
          i18nStatusKey: signatureStatus === DocumentSignatureStatus.Completed ? 'signed-at' : 'completed-at',
          date: submittedAt,
          contact: null,
        } as const;
      default:
        return null;
    }
  }, [
    status,
    updatedAt,
    updatedBy,
    completedAt,
    completedBy,
    sentForSignatureAt,
    sentForSignatureBy,
    signatureStatus,
    submittedAt,
  ]);

  return (
    <>
      <div className="flex items-start gap-4">
        {isInError && (
          <div className="mt-2 rounded-full border-2 border-red" title="">
            <ExclamationPointIcon className="size-6 fill-red" />
          </div>
        )}

        <div className="flex flex-col gap-1">
          <div className="font-bold text-violet">{name}</div>

          <div className="text-xs text-charcoal">
            <div className="font-medium">
              {type}
              {/* eslint-disable-next-line i18next/no-literal-string */}
              {showVersion && ` (v${modelVersion})`}
            </div>
          </div>

          {owner && <AdvisorNameCodeTile owner={owner} advisorCode={advisorCode} />}

          <div className="text-xs text-grey">
            <DocumentStatusActionLabel i18nStatusKey="created-at" date={createdAt} contact={createdBy} />
            {statusText?.date && (
              <DocumentStatusActionLabel
                i18nStatusKey={statusText.i18nStatusKey}
                date={statusText.date}
                contact={statusText.contact}
              />
            )}
          </div>

          {errorText && <div className="text-xs text-red">{t(errorText)}</div>}
        </div>
      </div>
    </>
  );
};

export default ItemContent;
